import React, { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import currency from "currency.js";
import Images from "./image/Images";
import ButtonWithConfirmation from "./ButtonWithConfirmation";
import { generateItemSnippets } from "../util";

export default function LineItemV3(props) {
  const { item, onDecrease, onIncrease, onRemove } = props;
  const [optionsExpanded, setOptionsExpanded] = useState(false);

  const snippets = generateItemSnippets(item);
  const hasMultipleOptions = snippets.length > 1;
  const visibleSnippets = hasMultipleOptions && !optionsExpanded ? snippets.slice(0, 1) : snippets;
  const quantity = +item.quantity || 1;

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 1.5, md: 2 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          width: { xs: 60, md: 88 },
          flexShrink: 0,
          backgroundColor: "action.hover",
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <Images pics={item.images} />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.9375rem", md: "1.1875rem" },
            lineHeight: 1.3,
            color: "text.primary",
          }}
        >
          {item.title}
        </Typography>

        {visibleSnippets.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {visibleSnippets.map((snippet, idx) => (
              <Typography key={idx} variant="body2" color="textSecondary">
                {snippet}
              </Typography>
            ))}
          </Box>
        )}

        {hasMultipleOptions && (
          <Button
            type="button"
            size="small"
            onClick={() => setOptionsExpanded((prev) => !prev)}
            aria-expanded={optionsExpanded}
            endIcon={
              <ExpandMoreIcon
                aria-hidden="true"
                sx={{
                  fontSize: "1.1rem",
                  transform: optionsExpanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            }
            sx={{
              mt: 0.5,
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: { xs: "0.8125rem", md: "0.875rem" },
              color: "primary.main",
              "& .MuiButton-endIcon": { ml: 0.25 },
            }}
          >
            {optionsExpanded ? "Hide options" : "View options"}
          </Button>
        )}

        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.875rem", md: "0.9375rem" },
                color: "text.primary",
              }}
            >
              {currency(item.total || item.price).format()}
            </Typography>
            {onRemove && (
              <ButtonWithConfirmation
                copy="Are you sure you want to remove this item?"
                onConfirm={onRemove}
              >
                <IconButton
                  size="small"
                  aria-label="Remove item"
                  sx={{ color: "text.secondary", opacity: 0.8, p: 0.5 }}
                >
                  <DeleteOutlineIcon
                    aria-hidden="true"
                    sx={{ fontSize: { xs: "1.05rem", md: "1.15rem" } }}
                    color="error"
                  />
                </IconButton>
              </ButtonWithConfirmation>
            )}
          </Box>

          {onIncrease && onDecrease ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, md: 1 } }}>
              <IconButton
                onClick={onDecrease}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
                sx={{
                  width: { xs: 28, md: 34 },
                  height: { xs: 28, md: 34 },
                  border: "1px solid",
                  borderColor: "primary.main",
                  color: "primary.main",
                  backgroundColor: "transparent",
                  p: 0,
                  "&.Mui-disabled": {
                    borderColor: "action.disabled",
                    color: "action.disabled",
                  },
                }}
              >
                <RemoveIcon aria-hidden="true" sx={{ fontSize: { xs: "1rem", md: "1.15rem" } }} />
              </IconButton>
              <Typography
                aria-live="polite"
                sx={{
                  minWidth: "1.25rem",
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  color: "text.primary",
                }}
              >
                {quantity}
              </Typography>
              <IconButton
                onClick={onIncrease}
                aria-label="Increase quantity"
                sx={{
                  width: { xs: 28, md: 34 },
                  height: { xs: 28, md: 34 },
                  border: "1px solid",
                  borderColor: "primary.main",
                  color: "primary.main",
                  backgroundColor: "transparent",
                  p: 0,
                }}
              >
                <AddIcon aria-hidden="true" sx={{ fontSize: { xs: "1rem", md: "1.15rem" } }} />
              </IconButton>
            </Box>
          ) : (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.9375rem", md: "1rem" },
                color: "text.primary",
              }}
            >
              × {quantity}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
